import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Call02Icon,
  CallIncoming02Icon,
  CallOutgoing02Icon,
  Cancel01Icon,
  RefreshIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { CallRecordingPlayer } from "shared/components/CallRecordingPlayer";
import { CallTranscript } from "shared/components/CallTranscript";
import { cx, formatPhone } from "shared/utils";
import { MessageContentCard } from "./MessageContentCard";
import {
  MessageAddressField,
  type MessageAddress
} from "./MessageAddressField";
import { inboxApi } from "../api/api";
import type { VoiceDetails } from "../types";

// The response carries a presigned S3 link that boompay-api mints for an hour
// (BoomCRM::Call::PresignedRecordingUrl). The app disables refetch-on-focus
// globally, so freshness comes from the projection version in the query key
// plus this window, after which reopening the thread mints a new link.
const RECORDING_STALE_TIME_MS = 1000 * 60 * 5;

// Start fetching just before the card scrolls into view, so opening a thread
// with a long call history doesn't fire one request per card at once.
const PREFETCH_MARGIN = "200px";

// Terminal statuses, mirroring BoomCRM::Call's state machine. Anything else is
// still in flight (authorized / queued / ringing / in_progress).
const NO_ANSWER_STATUSES = new Set(["no_answer", "busy"]);
const UNREACHABLE_STATUSES = new Set([
  "failed",
  "canceled",
  "expired_before_dial"
]);
const TERMINAL_STATUSES = new Set([
  "completed",
  ...NO_ANSWER_STATUSES,
  ...UNREACHABLE_STATUSES
]);

const formatDuration = (seconds?: number | null): string | null => {
  if (seconds == null || seconds < 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

interface VoiceContentProps {
  isFromContact: boolean;
  voiceDetails: VoiceDetails;
  // Pre-resolved header rows from the thread, which can label a number the
  // projection has no name for (calls placed before from_name/to_name existed).
  // Falling back to the projection keeps the card self-sufficient.
  from?: MessageAddress;
  to?: MessageAddress;
  // Names the callee when neither the row nor the projection does.
  contactName?: string;
  onRedial?: () => void;
  onDelete?: () => void;
  className?: string;
  // Inbound call (CRM-905): "Incoming call" card; a missed call is an outcome
  // line, not a failure with Try again / Delete.
  isInbound?: boolean;
}

/**
 * The single call card the inbox renders for a voice message: who the call was
 * between, how it ended, and — once the call completes — its recording and
 * transcript, all inside one bubble.
 */
export const VoiceContent = ({
  isFromContact,
  voiceDetails,
  from: fromRow,
  to: toRow,
  contactName,
  onRedial,
  onDelete,
  className,
  isInbound
}: VoiceContentProps) => {
  const status = voiceDetails.call_status;
  const isCompleted = status === "completed";
  // An inbound call that never connected — no matter how Twilio spells it — is
  // simply missed; the Can't-reach failure UI describes outbound dialing only.
  const isNoAnswer = isInbound
    ? !isCompleted && TERMINAL_STATUSES.has(status)
    : NO_ANSWER_STATUSES.has(status);
  const isUnreachable = !isInbound && UNREACHABLE_STATUSES.has(status);

  const sectionRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = sectionRef.current;
    if (!el) return;

    // eslint-disable-next-line compat/compat -- IntersectionObserver is supported in all target browsers
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setInView(true);
      },
      { rootMargin: PREFETCH_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  // One request feeds both the Recording and the Transcript section: the API
  // returns a playable presigned URL alongside the transcript lines. Only a
  // completed call can have either, so a call that never connected fetches
  // nothing.
  //
  // The projection version is part of the key on purpose. The recording and the
  // transcript land seconds-to-minutes after the call ends (StoreRecordingJob,
  // then FetchTranscriptJob), each bumping the call's version — without it the
  // first response, taken the instant the call completed and therefore empty,
  // would stick and the card would never show either.
  const { data, isError, refetch, isFetching } = useQuery({
    queryKey: [
      "OUTBOUND_CALL_TRANSCRIPT",
      voiceDetails.call_id,
      voiceDetails.version
    ],
    queryFn: () => inboxApi.getOutboundCallTranscript(voiceDetails.call_id),
    enabled: isCompleted && inView,
    staleTime: RECORDING_STALE_TIME_MS
  });

  const from = fromRow?.address
    ? fromRow
    : {
        name: voiceDetails.from_name || (isInbound ? contactName : "") || "",
        address: voiceDetails.from_number || ""
      };
  const to = toRow?.address
    ? toRow
    : {
        name: voiceDetails.to_name || (isInbound ? "" : contactName) || "",
        address: voiceDetails.to_number || ""
      };
  // The non-partner side of the call — the callee for outbound, the caller for
  // inbound. Names the transcript's "User" speaker and hosts the redial icon.
  const externalParty = isInbound ? from : to;

  const duration = formatDuration(voiceDetails.duration_seconds);
  const transcript = data?.transcript ?? [];
  const recordingUrl = data?.recording_url;

  const outcome = (() => {
    if (isCompleted) return duration ? `Duration: ${duration}` : "Completed";
    if (isNoAnswer) return "No answer";
    if (isUnreachable) return null; // rendered as an error line instead
    return "Call in progress";
  })();

  return (
    <MessageContentCard
      isFromContact={isFromContact}
      className={cx(
        "flex flex-col gap-4 divide-y divide-alpha-40 [&>*]:pt-4 [&>*:first-child]:pt-0",
        className
      )}
    >
      <div ref={sectionRef} className="space-y-2 text-xs">
        <MessageAddressField
          label="From:"
          address={from}
          trailing={
            <Show if={!!onRedial && !!isInbound}>
              <CallBackButton party={externalParty} onRedial={onRedial} />
            </Show>
          }
        />
        <MessageAddressField
          label="To:"
          address={to}
          trailing={
            <Show if={!!onRedial && !isInbound}>
              <CallBackButton party={externalParty} onRedial={onRedial} />
            </Show>
          }
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-background-800">
          <HugeiconsIcon
            icon={isInbound ? CallIncoming02Icon : CallOutgoing02Icon}
            size={20}
            className="text-content-400"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-medium text-content-1000">
            {isInbound ? "Incoming call" : "Outbound call"}
          </p>
          <Show if={!!outcome}>
            <p className="text-xs font-medium text-content-200">{outcome}</p>
          </Show>
          <Show if={isUnreachable}>
            <div className="flex items-center gap-1.5 text-danger-500">
              <HugeiconsIcon icon={AlertCircleIcon} size={16} />
              <span className="text-xs font-medium">
                Can&apos;t reach this number
              </span>
            </div>
          </Show>
        </div>
        <Show if={isUnreachable}>
          <div className="flex shrink-0 items-center gap-2">
            <Show if={!!onRedial}>
              <Button
                HugeIcon={RefreshIcon}
                variant={ButtonVariant.BASE}
                size={ButtonSize.BASE}
                onClick={onRedial}
              >
                Try again
              </Button>
            </Show>
            <Show if={!!onDelete}>
              <Button
                HugeIcon={Cancel01Icon}
                variant={ButtonVariant.BASE}
                size={ButtonSize.BASE}
                iconStrokeWidth={3}
                onClick={onDelete}
              >
                Delete
              </Button>
            </Show>
          </div>
        </Show>
      </div>

      {/* Queries default to retry: false app-wide, so a single failure would
          otherwise make both sections vanish with no explanation. */}
      <Show if={isError}>
        <div className="flex items-center gap-2 text-xs font-medium text-content-200">
          <span>Couldn&apos;t load the recording and transcript.</span>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
          >
            {isFetching ? "Retrying…" : "Try again"}
          </button>
        </div>
      </Show>

      <Show if={!!recordingUrl}>
        <div>
          <p className="mb-1 text-xs font-medium text-content-200">Recording</p>
          <CallRecordingPlayer
            recordingUrl={recordingUrl ?? ""}
            durationSeconds={
              data?.recording_duration_seconds ??
              voiceDetails.recording_duration
            }
          />
        </div>
      </Show>

      {/* Wrapped so the transcript rows and their "Show more" toggle — which
          CallTranscript returns as sibling nodes — stay one divided section. */}
      <Show if={transcript.length > 0}>
        <div className="flex flex-col gap-2">
          <CallTranscript
            lines={transcript}
            contactName={
              externalParty.name || formatPhone(externalParty.address)
            }
          />
        </div>
      </Show>
    </MessageContentCard>
  );
};

// The small inline phone icon on the external party's row — redials the callee
// of an outbound call, or calls an inbound caller back.
const CallBackButton = ({
  party,
  onRedial
}: {
  party: MessageAddress;
  onRedial?: () => void;
}) => (
  <button
    type="button"
    onClick={onRedial}
    aria-label={`Call ${party.name || formatPhone(party.address)} again`}
    // Negative margin keeps the icon visually inline while the
    // padding gives the button a usable hit target.
    className="-m-1.5 p-1.5 text-content-300 hover:text-primary-500 transition-colors"
  >
    <HugeiconsIcon icon={Call02Icon} size={14} />
  </button>
);
